import React from 'react'

import { Checkbox, Skeleton, Table, Thumbnail } from 'components'
import { ActionEventTable, Row, TBody, THead } from 'components/Table'
import { TICKET_PRODUCTS_BY_BRANCH_HEADERS } from 'features/Ticket'
import { getImageFileName } from 'helpers/common'
import { isEmpty } from 'lodash'
import { TicketProductTable } from 'types'
import { useTranslation } from 'react-i18next'
import { PRODUCT_LIMIT } from 'helpers/constants'

interface CreateTicketProductListProps {
  isLoading: boolean
  isError: boolean
  products: TicketProductTable[]
  onSelectedProduct: (index: number) => void
  countProductSelected: number
}

export const CreateTicketProductList: React.FC<CreateTicketProductListProps> =
  ({
    isLoading,
    isError,
    products,
    onSelectedProduct,
    countProductSelected,
  }) => {
    const { t } = useTranslation('common')

    const base = 'con-ticket-products-by-branch'
    const columns = TICKET_PRODUCTS_BY_BRANCH_HEADERS

    const isDisabled = (product: TicketProductTable) => {
      return countProductSelected >= PRODUCT_LIMIT && !product.selected
    }

    const renderTable = (ElBody: React.ReactNode) => (
      <div className={base}>
        <Table>
          <THead
            className={`${base}__header-white`}
            columns={columns}
            position='left'
          />
          {ElBody}
        </Table>
      </div>
    )

    const SkeletonList: Record<string, React.ReactNode> = {
      selected: <Checkbox checked={false} name={'selected'} />,
      image: (
        <Skeleton.whiteSkeleton width='80px' height='80px' borderRadius='8px' />
      ),
      name: (
        <>
          <Skeleton.whiteSkeleton width='210px' />
          <Skeleton.whiteSkeleton width='290px' />
        </>
      ),
      serialNumber: <Skeleton.whiteSkeleton width='136px' />,
    }

    if (isLoading) {
      return renderTable(
        <Skeleton.SkeletonList
          columns={columns}
          rowOdd={SkeletonList}
          rowEven={SkeletonList}
          loopOptional={6}
        />,
      )
    }

    if (isEmpty(products) || isError) {
      return renderTable(
        <ActionEventTable
          colSpan={columns.length}
          isNotFound
          customNotFoundText={t('dataNotFound.products.label')}
        />,
      )
    }

    const list: Record<string, React.ReactNode>[] = products.map(
      (product, index) => {
        return {
          selected: (
            <Checkbox
              checked={Boolean(product.selected)}
              name={'selected' + index}
              onChange={() => {
                onSelectedProduct(index)
              }}
              disabled={isDisabled(product)}
              error={countProductSelected === 0}
            />
          ),
          image: (
            <Thumbnail
              className={`${base}__thumbnail`}
              src={getImageFileName(product.photos?.[0] || '')}
            />
          ),
          name: product.name,
          serialNumber: product.serialNumber,
        }
      },
    )

    return renderTable(
      <TBody id={'scrollableDiv'}>
        {list.map((data, index) => (
          <Row key={index} columns={columns} data={data} />
        ))}
      </TBody>,
    )
  }
